<template>
  <node-view-wrapper as="div" class="info-prices-block not-prose" contenteditable="false">
    <block-controls :editor="editor" :get-pos="getPos" :delete-node="deleteNode" :label="$t('products.block_info_prices')" />

    <!-- Preview: same inline styles as the saved HTML (see infoPricesStyles.js) -->
    <div :style="styles.card">
      <p v-if="node.attrs.title" :style="styles.title">{{ node.attrs.title }}</p>
      <div v-if="visibleRows.length" :style="styles.rows">
        <span v-for="row in visibleRows" :key="row.index" :style="rowStyle(row.index)">
          <span v-if="row.label">{{ row.label }}</span>
          <strong v-if="row.amount">{{ row.amount }}</strong>
        </span>
      </div>
    </div>

    <!-- Form -->
    <div class="mt-3 space-y-3">
      <div>
        <label :for="fieldId('title')" class="info-prices-label">{{ $t('products.info_prices_title') }}</label>
        <input :id="fieldId('title')" type="text" :value="node.attrs.title" class="info-prices-input"
               :placeholder="$t('products.info_prices_title_placeholder')"
               @input="updateAttributes({ title: $event.target.value })" />
      </div>

      <!-- Rows are only added/removed (never reordered), so the index is a stable key -->
      <fieldset v-for="(row, index) in rows" :key="index"
                class="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
        <legend class="sr-only">{{ $t('products.info_prices_row', { n: index + 1 }) }}</legend>
        <div>
          <label :for="fieldId('label-' + index)" class="info-prices-label">{{ $t('products.info_prices_row_label') }}</label>
          <input :id="fieldId('label-' + index)" type="text" :value="row.label" class="info-prices-input"
                 :placeholder="$t('products.info_prices_row_label_placeholder')"
                 @input="updateRow(index, 'label', $event.target.value)" />
        </div>
        <div>
          <label :for="fieldId('amount-' + index)" class="info-prices-label">{{ $t('products.info_prices_row_amount') }}</label>
          <input :id="fieldId('amount-' + index)" type="text" :value="row.amount" class="info-prices-input"
                 :placeholder="$t('products.info_prices_row_amount_placeholder')"
                 @input="updateRow(index, 'amount', $event.target.value)" />
        </div>
        <button type="button" @click="removeRow(index)" :disabled="rows.length <= 1"
                :title="$t('products.info_prices_remove_row', { n: index + 1 })"
                :aria-label="$t('products.info_prices_remove_row', { n: index + 1 })"
                class="justify-self-start p-2 rounded text-red-600 hover:text-red-400 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </fieldset>

      <div class="flex flex-wrap items-end justify-between gap-3">
        <button type="button" @click="addRow" :disabled="rows.length >= maxRows"
                class="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-bpblue">
          {{ $t('products.info_prices_add_row') }}
        </button>
        <div>
          <label :for="fieldId('accent-color')" class="info-prices-label">{{ $t('products.info_prices_accent_color') }}</label>
          <input :id="fieldId('accent-color')" type="color" :value="accentColor"
                 class="mt-1 h-9 w-16 cursor-pointer rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-bpblue"
                 @input="updateAttributes({ accentColor: normalizeHexColor($event.target.value) || defaultAccentColor })" />
        </div>
      </div>
    </div>
  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'
import BlockControls from './BlockControls.vue'
import { normalizeHexColor } from './colorAttributes'
import { DEFAULT_ACCENT_COLOR, MAX_INFO_PRICES_ROWS, INFO_PRICES_STYLES, infoPricesAccentStyle } from './infoPricesStyles'

export default {
  name: 'InfoPricesNodeView',

  components: {
    NodeViewWrapper,
    BlockControls,
  },

  props: nodeViewProps,

  data() {
    return {
      styles: INFO_PRICES_STYLES,
      maxRows: MAX_INFO_PRICES_ROWS,
      defaultAccentColor: DEFAULT_ACCENT_COLOR,
    }
  },

  computed: {
    rows() {
      const rows = Array.isArray(this.node.attrs.rows) ? this.node.attrs.rows : []
      return rows.length ? rows : [{ label: '', amount: '' }]
    },
    // Same rule as renderHTML: a row is hidden only when both fields are empty.
    visibleRows() {
      return this.rows
        .map((row, index) => ({ index, label: row.label || '', amount: row.amount || '' }))
        .filter(row => row.label || row.amount)
    },
    accentColor() {
      return normalizeHexColor(this.node.attrs.accentColor) || DEFAULT_ACCENT_COLOR
    },
  },

  methods: {
    normalizeHexColor,
    fieldId(name) {
      return `info-prices-${this.$.uid}-${name}`
    },
    rowStyle(index) {
      return this.styles.row + (index === 0 ? infoPricesAccentStyle(this.accentColor) : this.styles.neutralRow)
    },
    // Always a new array: node attributes are never mutated in place.
    updateRow(index, field, value) {
      this.updateAttributes({ rows: this.rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)) })
    },
    addRow() {
      if (this.rows.length >= MAX_INFO_PRICES_ROWS) return
      this.updateAttributes({ rows: [...this.rows, { label: '', amount: '' }] })
    },
    removeRow(index) {
      if (this.rows.length <= 1) return
      this.updateAttributes({ rows: this.rows.filter((row, i) => i !== index) })
    },
  },
}
</script>

<style scoped>
.info-prices-block {
  margin: 12px 0;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #ffffff;
}

.info-prices-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
}

.info-prices-input {
  margin-top: 0.25rem;
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
}

.info-prices-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #3297cc;
}
</style>
